import moment from "moment"

const TIME_FORMATS = ["HH:mm:ss", "HH:mm", "h:mm A", "hh:mm A"]

const formatTime = (value) => (value ? moment(value, TIME_FORMATS).format("h:mm A") : "")

/**
 * Full, human time range with date for the hover popover, e.g.
 * "Mon, Jun 29 · 7:00 AM – 8:00 AM" (single day) or the start only for multi-day.
 */
export const formatFullTimeRange = ({ date, start, end, multiDay }) => {
  const dateLabel = date ? moment(date).format("ddd, MMM D") : ""
  const startLabel = formatTime(start)
  const endLabel = multiDay ? "" : formatTime(end)
  const timePart = [startLabel, endLabel].filter(Boolean).join(" – ")
  return [dateLabel, timePart].filter(Boolean).join(" · ")
}

/** Sum of adult/child/infant counts as the party size. */
export const getGuestCount = ({ adult_count, child_count, infant_count } = {}) =>
  (Number(adult_count) || 0) + (Number(child_count) || 0) + (Number(infant_count) || 0)

/** "5 guests" with a breakdown when there are non-adult guests. */
export const getGuestLabel = (source = {}) => {
  const total = getGuestCount(source)
  if (total <= 0) return ""
  const base = `${total} ${total === 1 ? "guest" : "guests"}`
  const parts = []
  const adults = Number(source.adult_count) || 0
  const children = Number(source.child_count) || 0
  const infants = Number(source.infant_count) || 0
  if (children > 0 || infants > 0) {
    if (adults > 0) parts.push(`${adults} adult${adults === 1 ? "" : "s"}`)
    if (children > 0) parts.push(`${children} child${children === 1 ? "" : "ren"}`)
    if (infants > 0) parts.push(`${infants} infant${infants === 1 ? "" : "s"}`)
  }
  return parts.length ? `${base} (${parts.join(", ")})` : base
}

/** Assigned equipment/crew names for the "resource" row; null when nothing is assigned. */
export const getResourceName = ({ assigned_equipment = [] } = {}) => {
  const names = [...(assigned_equipment || []).map((item) => item?.name || item?.equipment_title)].filter(Boolean)
  return names.length ? names.join(", ") : null
}

export const WAIVER_STATUS = {
  SIGNED: "signed",
  PENDING: "pending"
}

/** Whether a waiver is attached to the booking — gates the badge entirely. */
export const isWaiverAttached = (source = {}) => Boolean(source.is_waiver_attached)

/** Number of guests still pending a signed waiver. */
export const getPendingWaiverCount = (source = {}) => {
  const total = Number(source.total_guest_count ?? source.totalGuestCount) || 0
  const signed = Number(source.signed_waiver_count ?? source.signedCount) || 0
  return Math.max(total - signed, 0)
}

/**
 * Derives the waiver badge status for a booking tile. A booking is "signed"
 * only when every guest's waiver is signed (signed count === total guest
 * count); otherwise it is "pending".
 */
export const getWaiverStatus = (source = {}) =>
  getPendingWaiverCount(source) === 0 ? WAIVER_STATUS.SIGNED : WAIVER_STATUS.PENDING

/** Short badge label: "Signed" or "{n} pending". */
export const getWaiverBadgeLabel = (source = {}) => {
  const pending = getPendingWaiverCount(source)
  return pending === 0 ? "Signed" : `${pending} pending`
}

/** Human waiver label for the popover, e.g. "Waiver signed" or
 * "Waiver pending (2 of 3 pending)" when guest counts are available. */
export const getWaiverLabel = (source = {}) => {
  const pending = getPendingWaiverCount(source)
  const total = Number(source.total_guest_count ?? source.totalGuestCount) || 0
  if (pending === 0) return "Waiver signed"
  return total > 0 ? `Waiver pending (${pending} of ${total} pending)` : "Waiver pending"
}

/**
 * Normalizes either a calendar grid `event` (Day/Week) or a raw `booking`
 * (Month) into the shape the hover popover renders. Field fallbacks let both
 * shapes flow through the same builder without per-view forks.
 */
export const buildBookingPopoverModel = (source = {}) => {
  const title = source.title || source.experience_name || ""
  const start = source.start || source.start_time
  const end = source.end || source.end_time
  const resourceName = getResourceName(source)
  // Day/Week map the raw booking into an `events` object and carry the trip
  // name over as `experienceName` (title is client_name there, so it can't
  // double as the trip name). Month passes the raw booking straight through,
  // which only ever has the snake_case `experience_name`, so both are checked.
  const experienceName = source.experienceName || source.experience_name || ""
  return {
    title,
    experienceName,
    timeText: formatFullTimeRange({ date: source.date, start, end, multiDay: source.multi_day }),
    resourceName,
    clientName: source.client_name || "",
    guestLabel: getGuestLabel(source),
    location: source.location || "",
    waiverStatus: getWaiverStatus(source),
    waiverLabel: isWaiverAttached(source) ? getWaiverLabel(source) : ""
  }
}
